import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { api } from "../lib/api";
import i18n from "../lib/i18n";

interface Entry {
  id: string;
  version: string;
  releasedAt: string;
  kind: "added" | "changed" | "fixed" | "removed" | "security";
  summaryEn: string;
  summaryEs: string;
}

const KIND_VARIANT: Record<
  Entry["kind"],
  "success" | "info" | "warning" | "secondary" | "destructive"
> = {
  added: "success",
  changed: "info",
  fixed: "warning",
  removed: "secondary",
  security: "destructive",
};

/** What's New (stack §17b). Entries are authored bilingually EN/ES. */
export default function Changelog() {
  const { t } = useTranslation();
  const spanish = (i18n.resolvedLanguage ?? "en").startsWith("es");

  const { data, isLoading } = useQuery({
    queryKey: ["changelog"],
    queryFn: () => api<Entry[]>("/api/v1/changelog"),
  });

  const byVersion = new Map<string, Entry[]>();
  for (const e of data ?? []) {
    const list = byVersion.get(e.version) ?? [];
    list.push(e);
    byVersion.set(e.version, list);
  }

  return (
    <>
      <h1>{t("navigation:changelog")}</h1>
      {isLoading && <p className="mt-4 text-muted-foreground">{t("common:loading")}</p>}

      {[...byVersion.entries()].map(([version, entries]) => (
        <section key={version} className="mt-8">
          <h2>
            v{version}
            <span className="ml-3 text-base font-normal text-muted-foreground">
              {entries[0] &&
                new Date(entries[0].releasedAt).toLocaleDateString(i18n.resolvedLanguage)}
            </span>
          </h2>
          <ul className="mt-3 space-y-2">
            {entries.map((e) => (
              <li key={e.id}>
                <Card className="flex flex-col gap-1 p-3 sm:flex-row sm:items-start sm:gap-3">
                  <Badge variant={KIND_VARIANT[e.kind]} className="w-fit">
                    {t(`common:changeKind.${e.kind}`)}
                  </Badge>
                  <span>{spanish ? e.summaryEs : e.summaryEn}</span>
                </Card>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </>
  );
}
